/**
 * Get Avatar Asset Helper
 * CDN: folder = avatar name, files = sad.png, angry.png, idle.png, happy.png
 * Fallbacks: if sad doesn't exist use angry (losing); if happy doesn't exist use idle (winning).
 */

import type { ImageSourcePropType } from 'react-native';
import type { AvatarMood, AvatarHomeAway } from '../types/avatar.types';
import type { DisplayAvatarId } from '../types/avatar.types';
import { AVATAR_REGISTRY, AVATAR_REGISTRY_SM } from '../../../constants/avatarRegistry';
import { DEFAULT_AVATAR_ID } from '../../../constants/avatarIds';

export function imageSourceKey(src: ImageSourcePropType): string {
  if (src && typeof src === 'object' && 'uri' in src && typeof (src as { uri: unknown }).uri === 'string') {
    return (src as { uri: string }).uri;
  }
  return JSON.stringify(src);
}

/**
 * Ordered sources for losing mood: try sad, then angry (when sad asset missing on CDN), then idle.
 */
export function getSadMoodSourceChain(
  avatarId: DisplayAvatarId,
  small?: boolean
): ImageSourcePropType[] {
  try {
    const registry = small ? AVATAR_REGISTRY_SM : AVATAR_REGISTRY;
    const avatarEntry = registry[avatarId] ?? registry[DEFAULT_AVATAR_ID];
    if (!avatarEntry) return [];

    const ordered: (ImageSourcePropType | null | undefined)[] = [
      avatarEntry.sad,
      avatarEntry.angry,
      avatarEntry.idle,
    ];

    const out: ImageSourcePropType[] = [];
    const seen = new Set<string>();
    for (const src of ordered) {
      if (!src) continue;
      const key = imageSourceKey(src);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(src);
    }
    return out;
  } catch {
    return [];
  }
}

/**
 * Get avatar image asset for the specified avatar ID and mood.
 * Uses fallbacks: sad -> angry when sad missing; happy -> idle when happy missing.
 * @param small - When true, uses the 150×150 CDN variant.
 */
export const getAvatarAsset = (
  avatarId: DisplayAvatarId,
  mood: AvatarMood,
  small?: boolean
): ImageSourcePropType | null => {
  try {
    const registry = small ? AVATAR_REGISTRY_SM : AVATAR_REGISTRY;
    const avatarEntry = registry[avatarId] ?? registry[DEFAULT_AVATAR_ID];
    if (!avatarEntry) return null;

    if (mood === 'sad') return avatarEntry.sad ?? avatarEntry.angry ?? null;
    if (mood === 'happy') return avatarEntry.happy ?? avatarEntry.idle ?? null;
    return avatarEntry[mood] ?? null;
  } catch {
    return null;
  }
};

/**
 * Fallback source when primary mood image fails to load (e.g. 404 on CDN).
 * Losing: try sad then angry; winning: try happy then idle.
 * @param small - When true, uses the 150×150 CDN variant.
 */
export const getAvatarFallbackAsset = (
  avatarId: DisplayAvatarId,
  mood: AvatarMood,
  small?: boolean
): ImageSourcePropType | null => {
  try {
    const registry = small ? AVATAR_REGISTRY_SM : AVATAR_REGISTRY;
    const avatarEntry = registry[avatarId] ?? registry[DEFAULT_AVATAR_ID];
    if (!avatarEntry) return null;
    if (mood === 'sad' && avatarEntry.angry) return avatarEntry.angry;
    if (mood === 'happy' && avatarEntry.idle) return avatarEntry.idle;
    return null;
  } catch {
    return null;
  }
};

/**
 * Get avatar image for success screen: home = challenger (us), away = opponent (them).
 * Falls back to idle when home/away is missing.
 * @param small - When true, uses the 150×150 CDN variant.
 */
export const getAvatarHomeAwayAsset = (
  avatarId: DisplayAvatarId,
  variant: AvatarHomeAway,
  small?: boolean
): ImageSourcePropType | null => {
  try {
    const registry = small ? AVATAR_REGISTRY_SM : AVATAR_REGISTRY;
    const avatarEntry = registry[avatarId] ?? registry[DEFAULT_AVATAR_ID];
    if (!avatarEntry) return null;
    const source = variant === 'home' ? avatarEntry.home : avatarEntry.away;
    return source ?? avatarEntry.idle ?? null;
  } catch {
    return null;
  }
};

/**
 * Ordered URI attempts for Image onError chaining (sad → angry → idle; happy → idle; etc.).
 */
export function getAvatarLoadAttempts(
  avatarId: DisplayAvatarId,
  mood: AvatarMood,
  variant: AvatarHomeAway | undefined,
  small?: boolean
): ImageSourcePropType[] {
  if (variant) {
    const p = getAvatarHomeAwayAsset(avatarId, variant, small);
    return p ? [p] : [];
  }
  if (mood === 'sad') {
    return getSadMoodSourceChain(avatarId, small);
  }
  const primary = getAvatarAsset(avatarId, mood, small);
  const secondary = getAvatarFallbackAsset(avatarId, mood, small);
  const out: ImageSourcePropType[] = [];
  if (primary) out.push(primary);
  if (secondary && (!primary || imageSourceKey(secondary) !== imageSourceKey(primary))) {
    out.push(secondary);
  }
  return out;
}
